import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Skeleton } from 'primereact/skeleton';
import { Column } from 'primereact/column';
import './atlas-perspectives-details.scss';
import { Message } from 'primereact/message';
import { PermissionList } from '../../../../../types/sharedTypes'; // Import PermissionList
import { v4 as uuidv4 } from 'uuid';
import { SphereAvatar } from '../../../../design-system-my-lib';
import DialogWrapper from '../../DialogWrapper/DialogWrapper';
import { Sphere } from 'types/global';
import { Dropdown } from 'primereact/dropdown';
import PerspectiveSelector from '../../PerspectiveSelector/PerspectiveSelector';
import { Toast } from 'primereact/toast';
import { useApiClientsOrNull } from '../../../../context/ApiClientsContext'; // Import useApiClientsOrNull

interface DataTableProps {
  columns: { field: string; header: string }[];
  sphereId: string;
  sphere?: Sphere;
  options: any[];
  onChange: (e: { value: any[] }) => void;
  setSelectedResponsiblePerspective: React.Dispatch<
    React.SetStateAction<any[]>
  >;
  value?: any[];
  permissionList: PermissionList; // Add permissionList prop
  selectedPerspectiveSystemName?: string;
  setSelectedPerspectiveSystemName: React.Dispatch<
    React.SetStateAction<string | null>
  >;
  onParticipantSelect: (participant: any) => void;
}

interface RowData {
  properties: { name: string };
  perspectives: any[];
}

const AtlasPerspectiveDetails: React.FC<DataTableProps> = ({
  columns,
  sphereId,
  sphere,
  options,
  onChange,
  setSelectedResponsiblePerspective,
  value,
  permissionList,
  selectedPerspectiveSystemName,
  setSelectedPerspectiveSystemName,
  onParticipantSelect,
}) => {
  const apiClients = useApiClientsOrNull();
  
  if (!apiClients) {
    return null;
  }
  
  const { graphClient } = apiClients;
  const searchQuery = ''; // State for search query
  const [perspectiveDetails, setPerspectiveDetails] = useState<any[]>([]); // State to store API data
  const [loading, setLoading] = useState<boolean>(true); // Loading state
  const [error, setError] = useState<string | null>(null); // Error state
  const [selectedRow, setSelectedRow] = useState<RowData | null>(null);
  const [, setUpdatedSelectedOptions] = useState<any[]>(options);
  const [addAssigneeDialogVisible, setAddAssigneeDialogVisible] =
    useState<boolean>(false);
  const [selectedAssignees, setSelectedAssignees] = useState<string[] | null>(
    null
  );
  const toastRef = React.useRef<Toast>(null);

  // Handle single assignee selection
  const handleAssigneeSelected = (
    assignee: string | null,
    sphereId: string,
    perspectiveSystemName: string,
    assigneeSphere?: Sphere
  ) => {
    if (assignee === null) {
      setSelectedAssignees(null);
    } else {
      setSelectedAssignees([assignee]);
    }
  };

  // Handle multiple assignees selection
  const handleAssigneesSelected = (
    assignees: string[] | null,
    sphereId: string,
    perspectiveSystemName: string,
    assigneeSpheres?: Sphere[]
  ) => {
    setSelectedAssignees(assignees);
  };

  // Handlers for dialog open/close
  const openAddAssigneeDialog = () => setAddAssigneeDialogVisible(true);

  const closeAddAssigneeDialog = () => {
    setAddAssigneeDialogVisible(false);
  };

  const saveAssignees = async () => {
    try {
      // Ensure user has manage_security permission
      if (!manage_security) {
        throw new Error('User does not have permission to manage security');
      }

      // Find the currently selected perspective ID and full perspective object
      const selectedPerspectiveObj = sphere?.perspectives?.find(
        (p) => p.properties.system_name === selectedPerspectiveSystemName
      );
      const perspectiveId = selectedPerspectiveObj?.id;
      if (!perspectiveId) {
        throw new Error('No perspective selected');
      }

      // Ensure at least one assignee was chosen
      if (!selectedAssignees || selectedAssignees.length === 0) {
        throw new Error('No assignees selected');
      }

      // Get currently assigned subjects for this perspective
      const existingAssignment = sphere?.assignedPerspectives?.find(
        (ap) => ap.perspectiveProperties.system_name === selectedPerspectiveSystemName
      );
      const currentlyAssignedIds = existingAssignment?.assigned.map(a => a.sphere.id) || [];

      // Calculate which subjects to add and which to remove
      const selectedIds = selectedAssignees;
      const idsToAdd = selectedIds.filter(id => !currentlyAssignedIds.includes(id));
      const idsToRemove = currentlyAssignedIds.filter(id => !selectedIds.includes(id));

      // For single-cardinality perspectives, unassign existing assignee first
      const isSingleCardinality = selectedPerspectiveObj?.properties?.cardinality === 'single';
      if (isSingleCardinality && idsToRemove.length > 0) {
        // Unassign the existing subject
        await graphClient.delete<unknown>(
          `/perspectives/${encodeURIComponent(perspectiveId)}/subjects/${encodeURIComponent(idsToRemove[0])}`
        );
      }

      // For multi-cardinality perspectives, unassign removed subjects
      if (!isSingleCardinality && idsToRemove.length > 0) {
        await Promise.all(
          idsToRemove.map((subjectId) =>
            graphClient.delete<unknown>(
              `/perspectives/${encodeURIComponent(perspectiveId)}/subjects/${encodeURIComponent(subjectId)}`
            )
          )
        );
      }

      // Assign only the new subjects (not already assigned)
      if (idsToAdd.length > 0) {
        await Promise.all(
          idsToAdd.map((subjectId) =>
            graphClient.post<unknown>(
              `/perspectives/${encodeURIComponent(perspectiveId)}/assign-subject`,
              { subjectId, urgency: 3 }
            )
          )
        );
      }

      toastRef.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Assignees updated successfully',
      });

      // Refresh the list after assignment
      if (sphereId) {
        await fetchPerspectiveDetails(sphereId);
      }
    } catch (error: any) {
      console.error('Error saving assignees:', error);
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.message ?? 'An error occurred',
      });
    } finally {
      setSelectedAssignees(null);
      closeAddAssigneeDialog();
    }
  };

  // Perspective dropdown options
  const perspectiveOptions =
    sphere?.perspectives
      ?.sort((a, b) => a.properties.name.localeCompare(b.properties.name))
      .map((perspective) => ({
        label: perspective.properties.name,
        value: perspective.properties.system_name,
      })) || [];

  const handlePerspectiveChange = (e: { value: string }) => {
    setSelectedPerspectiveSystemName(e.value);
  };

  // Get the selected perspective's cardinality
  const selectedPerspective = sphere?.perspectives?.find(
    (p) => p.properties.system_name === selectedPerspectiveSystemName
  );
  const isMultiCardinality = selectedPerspective?.properties?.cardinality === 'multiple';

  // --- Permission Logic ---
  const { view, edit, manage_security } = permissionList.permissions;

  // API call function to fetch perspective details
  const fetchPerspectiveDetails = async (sphereId: string) => {
    try {
      // Fetch subjects with their perspectives via the ApiClient
      const resp = await graphClient.get<{
        data: { subjects: any[] };
      }>(`/spheres/${encodeURIComponent(sphereId)}/subjects-with-perspectives`);

      // Update local state with the subjects array
      setPerspectiveDetails(resp.data.subjects);
    } catch (err) {
      console.error('Error fetching perspective details:', err);
      setError('No subject assignments available at the moment');
    } finally {
      setLoading(false);
    }
  };

  // useEffect to call the API when the sphereId changes or on component mount
  useEffect(() => {
    if (sphereId) {
      fetchPerspectiveDetails(sphereId);
    }
  }, [sphereId]);

  if (!view) {
    // If the user doesn't have view permissions, do not render anything
    return null;
  }

  // If user can view but cannot edit, the component should be read-only
  const isReadOnly = view && !edit;

  // Custom body template for the subjects column with an icon
  const subjectsBodyTemplate = (rowData: any) => {
    return (
      <div className="subject-column">
        <div>
          <span>
            <SphereAvatar
              glyph={rowData.glyph}
              glyphColor={rowData.glyphColor}
              sphereId={rowData.id}
              imageId={rowData.properties?.imageId}
              sphereName={rowData.properties?.name}
            />
          </span>
          <label className="ml-1 wrap-text" style={{ verticalAlign: 'super' }}>
            {rowData.properties.name}
          </label>
        </div>
      </div>
    );
  };

  // Custom body template for the perspectives column
  const perspectivesBodyTemplate = (rowData: any) => {
    return (
      <div
        className="perspectives-column"
        style={{ display: 'flex', alignItems: 'center' }}
      >
        {rowData.perspectives?.map((perspective: any, index: number) => (
          <React.Fragment
            key={perspective.id || perspective.properties?.name || index}
          >
            <div
              style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}
              className="ml-2"
            >
              {perspective.properties?.name}
            </div>
          </React.Fragment>
        ))}
      </div>
    );
  };

  // Filter data based on the search query for all fields
  const filteredData =
    perspectiveDetails?.filter((row) => {
      const combinedFields = `${row.name || ''} ${(row.perspectives || [])
        .map((p: any) => p.name || '')
        .join(' ')} ${row.description || ''}`.toLowerCase();

      return combinedFields.includes(searchQuery.toLowerCase()); // Check if the combined fields contain the search query
    }) || [];

  // Handle selection changes when a row is clicked
  const onRowSelect = (e: any) => {
    if (isReadOnly) {
      // Do not allow row selection if read-only
      return;
    }

    setSelectedRow(e.value);

    // Check if e.value is an array for selection arrays (like in multiple selection mode)
    const rowData = Array.isArray(e.value) ? e.value[0] : e.value;

    // If no perspectives data or no row is selected, return
    if (!rowData?.perspectives) {
      return;
    }

    // Create a map from perspective names to descriptions, checking both `properties.description` and `description` fields
    const perspectiveMap = new Map(
      (rowData.perspectives || []).map((perspective: any) => [
        perspective.properties?.name || perspective.label,
        perspective.properties?.description ||
          perspective.description ||
          'No description available',
      ])
    );

    // Construct `updatedSelectedOptions` with descriptions for each option
    const updatedOptions = options.map((option) => {
      const description =
        perspectiveMap.get(option.label) ||
        option.description ||
        'No description available';
      return {
        ...option,
        selected: perspectiveMap.has(option.label), // Mark as selected if it's in perspectiveMap
        disabled:
          option.label === 'Responsible' && perspectiveMap.has('Responsible'), // Keep 'Responsible' option disabled when selected
        description, // Retain or set description for each option
      };
    });

    setUpdatedSelectedOptions(updatedOptions); // Update the state with all descriptions
    setSelectedResponsiblePerspective(updatedOptions); // Sync with parent
    onChange({ value: updatedOptions }); // Notify parent component

    // Call the parent's callback with the selected row (participant)
    if (onParticipantSelect) {
      onParticipantSelect(rowData); // Notify parent component of selected participant
    }
  };

  // Apply conditional row styling based on selection
  const rowClassName = (rowData: any) => {
    let isSelected = false;
    if (selectedRow) {
      if (Array.isArray(selectedRow)) {
        isSelected = selectedRow.some(
          (row) => row.properties?.name === rowData.properties?.name
        );
      } else {
        isSelected = selectedRow.properties?.name === rowData.properties?.name;
      }
    }

    return {
      'highlighted-row': isSelected, // Highlight selected row
    };
  };

  const renderSkeletonRows = () => {
    const rowUUIDs = Array.from({ length: 5 }, () => uuidv4()); // Pre-generate UUIDs for rows

    return (
      <React.Fragment>
        {rowUUIDs.map((rowId) => (
          <tr key={`skeleton-row-${rowId}`}>
            {columns.map((col) => (
              <td key={`skeleton-${col.field}-${rowId}`}>
                <Skeleton height="1.5rem" />
              </td>
            ))}
          </tr>
        ))}
      </React.Fragment>
    );
  };

  if (loading) {
    return (
      <div className="card">
        <table className="skeleton-table">
          <tbody>{renderSkeletonRows()}</tbody>
        </table>
      </div>
    );
  }

  if (error) {
    return (
      <Message
        className="custom-error-message flex"
        severity="error"
        text={error}
      />
    );
  }

  const dataTableHeader = (
    <div className="detail-header">
      <h3>Perspective Assignments</h3>
      <i
        className="pi pi-user-plus add-assignee"
        onClick={openAddAssigneeDialog}
      />
    </div>
  );

  return (
    <div className="atlas-perspectives-card">
      <Toast ref={toastRef} />
      <DataTable
        header={dataTableHeader}
        className="atlas-perspectives-detail-grid"
        {...(!isReadOnly && { selectionMode: 'single' })}
        value={filteredData}
        rowClassName={rowClassName}
        selection={selectedRow}
        onSelectionChange={onRowSelect}
        scrollable
        scrollHeight="flex"
        tableStyle={{ cursor: isReadOnly ? 'not-allowed' : 'default' }}
      >
        {columns.map((col) => {
          let bodyTemplate;
          if (col.field === 'subjects') {
            bodyTemplate = subjectsBodyTemplate;
          } else if (col.field === 'perspectives') {
            bodyTemplate = perspectivesBodyTemplate;
          }

          return (
            <Column
              key={col.field}
              field={col.field}
              header={col.header}
              body={bodyTemplate}
            />
          );
        })}
      </DataTable>
      <DialogWrapper
        visible={addAssigneeDialogVisible}
        header="Add Assignee"
        onHide={closeAddAssigneeDialog}
        style={{ width: '500px' }}
      >
        <div className="perspective-select-wrapper">
          <label aria-controls="perspective-dropdown">Choose Perspective</label>
          <Dropdown
            key={'perspective-dropdown'}
            value={selectedPerspectiveSystemName}
            options={perspectiveOptions}
            onChange={handlePerspectiveChange}
            placeholder="Choose Perspective"
            className="p-mb-3 perspective-dropdown"
          />
          {selectedPerspectiveSystemName && (
            <PerspectiveSelector
              perspectiveSystemName={selectedPerspectiveSystemName}
              sphere={sphere!}
              organizationId={sphere?.properties.sphereId}
              showLabel={false}
              onAssigneesSelected={handleAssigneesSelected}
              onAssigneeSelected={handleAssigneeSelected}
              multi={isMultiCardinality}
              stayOpen={true}
            />
          )}
        </div>
        <div className="form-actions">
          <div className="save-button-container">
            <button
              className="button Save-button"
              onClick={saveAssignees}
            >
              Select
            </button>
          </div>
          <div className="close-button-container">
            <button className="Close-button" onClick={closeAddAssigneeDialog}>
              Close
            </button>
          </div>
        </div>
      </DialogWrapper>
    </div>
  );
};

export default AtlasPerspectiveDetails;
